import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from 'shared/layouts/Main'
import { Button, Form, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import InputWrapper from 'shared/components/InputWrapper'
import SelectWrapper from 'shared/components/SelectWrapper'
import resAddUser from './resolvers/resAddUser'
import useInit from './hooks/useInit'
import useFocusError from 'shared/hooks/useFocusError'
import prepareSubmitForm from 'shared/utils/prepareSubmitForm'
import submitForm from './utils/submitForm'
import { apiUrls, filterRoles } from 'consts'
import getBrandContent from 'utils/getBrandContent'
import Footer from 'shared/components/Footer'
import BackButton from 'shared/components/BackButton'
import AddRemoveTableSortable from 'components/AddRemoveTableSortable'
import AddRole from 'pages/AddRole'
import { togglePasswordInput, generateRandomPassword } from 'utils/passwordUtils'
import * as Icon from 'react-bootstrap-icons'
import Entity from 'components/AddRemoveTableSortable/types/Entity'
import AddCustomer from 'pages/AddCustomer'
import displayError from 'shared/utils/displayError'

const submitRef = React.createRef<HTMLButtonElement>()

const AddUser: React.FunctionComponent = () => {
  const [showOverlay, setShowOverlay] = useState<boolean>(true)
  const [dataLoaded, setDataLoaded] = useState<boolean>(false)
  const [relationalRoles, setRelationalRoles] = useState<Array<Entity>>([])
  const [relationalCustomers, setRelationalCustomers] = useState<Array<Entity>>([])
  const [includedCustomers, setIncludedCustomers] = useState<Array<Entity & {customer_id: string}>>([])
  const includedCustomersCountRef = useRef<number>(0)
  const [relationalCustomersInitialized, setRelationalCustomersInitialized] = useState<boolean>(false)

  const [passwordInputType, setPasswordInputType] = useState<string>('password')

  const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})

  const { id } = useParams()

  const isNewUser : boolean = id === undefined;

  const navigate = useNavigate()

  const {
    control,
    register,
    getValues,
    setValue,
    handleSubmit,
    reset,
    setFocus,
    setError,
    formState: { errors },
  } = useForm({
    mode: 'all',
    resolver: resAddUser,
    context: { isNewUser },
  })

  useInit({
    getValues,
    id,
    reset,
    setDataLoaded,
    setShowOverlay,
    setValue,
    currentCustomer,
  })

  useFocusError({
    errors,
    setFocus,
  })

  return (
    <Layout
      footerContent={<Footer />}
      brandContent={getBrandContent()}
      showOverlay={showOverlay}
      showCardHeader={true}
      headerInsert={<BackButton url='/users' text='All Users' />}
      actionInsert={
        <Button
          disabled={!dataLoaded}
          type='submit'
          name='save'
          className='btn-round btn-primary'
          onClick={() => {
            if (submitRef.current != null) {
              submitRef.current.click()
            }
          }}
        >
          Save User
        </Button>
      }
    >
      <>
        <Form
          onSubmit={handleSubmit((data) =>
            prepareSubmitForm({
              submitForm,
              navigate,
              data,
              setShowOverlay,
              setError,
              id,
              name: 'User',
              successNav: '/users',
              extraData: {
                roles: relationalRoles,
                customers: relationalCustomers,
              },
            }),
          )}
          id='main-form'
        >
          <InputWrapper label='Firstname' name='firstname' errors={errors}>
            <Form.Control type='text' {...register('firstname')} />
          </InputWrapper>

          <InputWrapper label='Lastname' name='lastname' errors={errors}>
            <Form.Control type='text' {...register('lastname')} />
          </InputWrapper>

          <InputWrapper label='Email' name='email' errors={errors}>
            <Form.Control type='email' {...register('email')} />
          </InputWrapper>

          {!id && (
            <InputWrapper label='Password' name='password_api' errors={errors}>
              <InputGroup className='mb-3'>
                <Form.Control type={passwordInputType} {...register('password_api')} />
                <OverlayTrigger
                  placement='top'
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip>Generate Password</Tooltip>}
                >
                  <Button
                    variant='outline-primary'
                    onClick={() => setValue('password_api', generateRandomPassword())}
                  >
                    <Icon.Key title='Generate random password' />
                  </Button>
                </OverlayTrigger>

                <OverlayTrigger
                  placement='top'
                  delay={{ show: 250, hide: 400 }}
                  overlay={
                    <Tooltip>
                      {passwordInputType === 'password' ? 'Show password' : 'Hide password'}
                    </Tooltip>
                  }
                >
                  <Button
                    variant='outline-warning'
                    onClick={() => setPasswordInputType(togglePasswordInput(passwordInputType))}
                  >
                    {passwordInputType === 'password' ? <Icon.Unlock /> : <Icon.Lock />}
                  </Button>
                </OverlayTrigger>
              </InputGroup>
            </InputWrapper>
          )}

          <InputWrapper label='Role' name='role' errors={errors}>
            <SelectWrapper
              name='role'
              isClearable={true}
              control={control}
              options={filterRoles}
              optionLabel='name'
              optionValue='value'
            />
          </InputWrapper>

          <InputWrapper label='Active' name='active' errors={errors}>
            <Form.Select {...register('active')}>
              <option key={0} value={1}>
                Yes
              </option>
              <option key={1} value={0}>
                No
              </option>
            </Form.Select>
          </InputWrapper>

          <Button type='submit' name='save' ref={submitRef} className='d-none'></Button>
        </Form>


        <AddRemoveTableSortable
          getAllRowsUrl={apiUrls.usersToCustomers + (id !== undefined ? '?id=' + id : '?add_default_customer=1')}
          addRemoveRowUrl={apiUrls.usersToCustomers}
          relationalFieldName='customer_id'
          toggleActiveUrl={apiUrls.customers}
          maxTableHeight='600px'
          entityNameSingular='Customer'
          entityNamePlural='Customers'
          setShowOverlay={setShowOverlay}
          editFormComponent={AddCustomer}
          onAddItem={
            id !== undefined
              ? undefined
              : (entity: Entity) => {
                  setRelationalCustomers((prevState) => [...prevState, entity])
                  return true
                }
          }
          onRemoveItem={
            id !== undefined
              ? undefined
              : (entity: Entity) => {
                  if(includedCustomersCountRef.current <= 1){
                    displayError({ errorMessage: 'User must be linked to at least 1 Customer'})
                    return false
                  }
                  setRelationalCustomers((prevState) => prevState.filter((r) => r.id !== entity.id))
                  return true
                }
          }
          onIncludedRowsChange={(rows: any[]) => {
            setIncludedCustomers(rows)
            if(isNewUser 
              && !relationalCustomersInitialized 
              && rows.length === 1 
              && relationalCustomers.length === 0
            ){
              setRelationalCustomersInitialized(true)
              setRelationalCustomers((prevState) => [...prevState, rows[0]])  
            }
            includedCustomersCountRef.current = rows.length
          }}
        />

        {includedCustomers.map((customer) => (
          <AddRemoveTableSortable
            key={customer.id}
            getAllRowsUrl={apiUrls.usersToRoles + '?id=' + id + '&customer_id=' + customer.customer_id!}
            addRemoveRowUrl={apiUrls.usersToRoles}
            relationalFieldName='role_id'
            toggleActiveUrl={apiUrls.roles}
            maxTableHeight='600px'
            entityNameSingular='Role'
            entityNamePlural={'Roles - ' + customer.name}
            setShowOverlay={setShowOverlay}
            editFormComponent={AddRole}
            onAddItem={
              id !== undefined
                ? undefined
                : (entity: Entity) => {
                  setRelationalRoles((prevState) => [...prevState, entity])
                    return true
                  }
            }
            onRemoveItem={
              id !== undefined
                ? undefined
                : (entity: Entity) => {
                  setRelationalRoles((prevState) => prevState.filter((r) => r.id !== entity.id))
                  return true
                }
              }
              />
        ))}
      </>
    </Layout>
  )
}

export default AddUser
